import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FieldHealthReportView from './fieldHealth/FieldHealthReportView';
import { downloadFieldHealthPdf, type FieldHealthReportInput } from '../utils/fieldHealthReportPdf';

interface ReportProps {
  reportData: FieldHealthReportInput;
  onClose: () => void;
  onExport?: () => void;
}

export default function Report({ reportData, onClose, onExport }: ReportProps) {
  const [isExporting, setIsExporting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [reportData]);

  const handleExportToPDF = async () => {
    setIsExporting(true);
    try {
      await downloadFieldHealthPdf(reportData);
      onExport?.();
    } catch (error) {
      console.error('PDF Export error:', error);
      alert('Failed to export PDF. Please ensure your browser allows downloads and try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={scrollRef}
      className="fixed inset-0 z-[200] overflow-y-auto overscroll-contain bg-[#e9ede8]/95"
    >
      <div className="mx-auto flex min-h-full w-full max-w-[860px] flex-col items-stretch px-4 pb-8 pt-4">
        <div className="sticky top-0 z-20 mb-3 flex justify-end gap-2 bg-[#e9ede8]/95 py-2 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => void handleExportToPDF()}
            disabled={isExporting}
            className={`inline-flex items-center gap-2 rounded-[10px] bg-[#008540] px-4 py-2 text-sm font-medium text-white shadow-lg transition hover:bg-[#0e5a2f] ${
              isExporting ? 'cursor-not-allowed opacity-60' : ''
            }`}
          >
            {isExporting ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Preparing PDF…
              </>
            ) : (
              <>
                <i className="ri-file-pdf-line" aria-hidden />
                Download PDF
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close report"
            className="grid h-10 w-10 place-items-center rounded-[10px] bg-white/95 text-xl text-[#6b7280] shadow-lg hover:bg-white"
          >
            ✕
          </button>
        </div>
        <FieldHealthReportView report={reportData} />
      </div>
    </div>,
    document.body
  );
}
